import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    Alert, Box, Chip, Drawer, IconButton, Snackbar, Switch, Typography, Button
} from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import React, { useEffect, useState } from 'react';
import { emailRegex } from '../../../constants/Constant';
import { IClosedLetterRejectReason, IClosedLetterRequest } from '../../../models/Inquiry/LetterDetails/IClosedLetterRequest';
import { useGlobalState } from '../../../store/GlobalStore';
import { useFetch, usePost } from '../../../utils/apiHelper';

interface RejectRequestDrawerProps {
    open: boolean;
    request: IClosedLetterRequest | null;
    onClose: () => void;
    onRejected: (requestId: number, reason: string, comments: string, rejectedByEmail: string) => void;
}

const MAX_RECIPIENTS = 10;

// Same case-file identity as EmailRequestDrawer, in the escalated/declined register:
// deep red in place of teal for the letterhead and primary action, everything else
// (neutrals, type roles, spacing rhythm) unchanged so the two drawers read as one system.
const ink = '#16233a';
const paper = '#f6f7f4';
const danger = '#b3261e';
const dangerDeep = '#8c1d17';
const line = '#dfe3de';
const serif = "'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif";
const mono = "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace";

// Contextual help for known reasons. Any reason added/renamed in dbo.ClosedLetterRejectReason
// without a matching entry here simply shows no help text - additive, never breaks the dropdown.
const reasonHelp: Record<string, string> = {
    'Cease and Desist': 'This account will not receive the requested letter or any further outbound correspondence.',
    'Bankruptcy': 'Collection activity is restricted while the bankruptcy status is in effect.',
    'Invalid/Undeliverable Address': 'The letter cannot be delivered to the address on file.',
    'Duplicate Request': 'An identical request has already been actioned for this account.',
    'Other': 'Add the specific reason in the comments below.',
};

const parseRecipients = (raw: string): string[] => {
    return raw
        .split(/[,;]/)
        .map((email) => email.trim())
        .filter((email) => email.length > 0);
};

interface RecipientFieldProps {
    label: string;
    required?: boolean;
    placeholder: string;
    emails: string[];
    input: string;
    onInputChange: (value: string) => void;
    onCommit: () => void;
    onRemove: (email: string) => void;
}

const RecipientField: React.FC<RecipientFieldProps> = ({
    label, required, placeholder, emails, input, onInputChange, onCommit, onRemove,
}) => (
    <Box flex={1} minWidth={0}>
        <Typography
            sx={{
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#6b7368', mb: '4px', display: 'block',
            }}
        >
            {label}{required ? <Box component="span" sx={{ color: danger }}> — required</Box> : null}
        </Typography>
        <Box
            sx={{
                height: 56,
                overflowY: 'auto',
                backgroundColor: 'white',
                border: `1px solid ${line}`,
                borderRadius: '4px',
                display: 'flex',
                flexWrap: 'wrap',
                alignContent: 'flex-start',
                gap: 0.5,
                padding: '8px',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:focus-within': { borderColor: danger, boxShadow: `0 0 0 1px ${danger}` },
            }}
        >
            {emails.map((email) => (
                <Chip
                    key={email}
                    label={email}
                    size="small"
                    onDelete={() => onRemove(email)}
                    sx={{ backgroundColor: '#f5eceb', fontSize: '0.78rem', fontFamily: mono, '& .MuiChip-deleteIcon': { fontSize: '16px' } }}
                />
            ))}
            <Box
                component="input"
                value={input}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
                        e.preventDefault();
                        onCommit();
                    }
                }}
                onBlur={onCommit}
                placeholder={emails.length === 0 ? placeholder : ''}
                sx={{
                    flex: '1 1 110px', minWidth: '110px', border: 'none', outline: 'none',
                    fontSize: '0.85rem', fontFamily: 'inherit', padding: '4px 2px',
                    backgroundColor: 'transparent', alignSelf: 'flex-start',
                }}
            />
        </Box>
    </Box>
);

const RejectRequestDrawer: React.FC<RejectRequestDrawerProps> = ({ open, request, onClose, onRejected }) => {
    const { state } = useGlobalState();
    const rejectedByEmail = state.user?.email || state.userAccessContext?.userName || '';

    const [reasons, setReasons] = useState<IClosedLetterRejectReason[]>([]);
    const [reason, setReason] = useState('');
    const [comments, setComments] = useState('');
    const [notifyRequester, setNotifyRequester] = useState(false);
    const [toInput, setToInput] = useState('');
    const [toEmails, setToEmails] = useState<string[]>([]);
    const [ccInput, setCcInput] = useState('');
    const [ccEmails, setCcEmails] = useState<string[]>([]);
    const [subject, setSubject] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successOpen, setSuccessOpen] = useState(false);

    const editor = useEditor({
        extensions: [StarterKit, Link],
        content: '',
    });

    // See EmailRequestDrawer.tsx for why this is needed: without destroying the editor on
    // unmount, ProseMirror can read scrollTop on its DOM node after the Drawer's exit transition
    // has already removed it, throwing an uncaught TypeError that blanks the whole app.
    useEffect(() => {
        return () => {
            editor?.destroy();
        };
    }, [editor]);

    useEffect(() => {
        if (open && request && editor && !editor.isDestroyed) {
            setReason('');
            setComments('');
            setNotifyRequester(false);
            setToEmails(request.requestedByEmail ? [request.requestedByEmail] : []);
            setToInput('');
            setCcEmails([]);
            setCcInput('');
            setSubject(`RE: Closed Letter Request — ${request.debtorNumber} / ${request.clientAccountNumber}`);
            setErrorMessage('');
            editor?.commands.setContent('<p></p>');
        }
    }, [open, request, editor]);

    useEffect(() => {
        if (!open || reasons.length > 0) return;
        useFetch<IClosedLetterRejectReason[]>('ClosedLetterRejectReasonsGet').then((response) => {
            setReasons(response.data || []);
        });
    }, [open]);

    // Cease and Desist means no outbound contact at all, so the notify toggle is force-disabled
    // and switched off the moment that reason is chosen, even if it was already on.
    useEffect(() => {
        if (reason === 'Cease and Desist') {
            setNotifyRequester(false);
        }
    }, [reason]);

    useEffect(() => {
        if (!notifyRequester || !reason) return;
        const isBodyBlank = (editor?.getText()?.trim() ?? '').length === 0;
        if (isBodyBlank) {
            const reasonText = reason === 'Other' && comments.trim()
                ? comments.trim()
                : reason;
            editor?.commands.setContent(
                `<p>Hello,</p><p></p><p>Your closed letter request has been declined.</p><p>Reason: ${reasonText}</p><p></p>`,
            );
        }
    }, [notifyRequester]);

    const commitRecipients = (raw: string, existing: string[], setList: (v: string[]) => void, setInput: (v: string) => void) => {
        const parsed = parseRecipients(raw);
        if (!parsed.length) return;

        const invalid = parsed.filter((email) => !emailRegex.test(email));
        if (invalid.length) {
            setErrorMessage(`Invalid email address: ${invalid.join(', ')}`);
            return;
        }

        const merged = Array.from(new Set([...existing, ...parsed]));
        setList(merged);
        setInput('');
        setErrorMessage('');
    };

    const removeToEmail = (email: string) => setToEmails(toEmails.filter((e) => e !== email));
    const removeCcEmail = (email: string) => setCcEmails(ccEmails.filter((e) => e !== email));

    const commentsRequired = reason === 'Other';
    const isBodyEmpty = () => (editor?.getText()?.trim() ?? '').length === 0;

    const isValid =
        !!reason &&
        (!commentsRequired || comments.trim().length > 0) &&
        (!notifyRequester || (
            toEmails.length > 0 &&
            toEmails.length + ccEmails.length <= MAX_RECIPIENTS &&
            subject.trim().length > 0 &&
            !isBodyEmpty()
        ));

    const handleClose = () => {
        if (submitting) return;
        onClose();
    };

    const handleConfirm = async () => {
        if (!request || !isValid) return;

        setSubmitting(true);
        setErrorMessage('');

        const payload = {
            requestId: request.id,
            rejectReason: reason,
            comments,
            rejectedByEmail,
            sendEmail: notifyRequester,
            fromEmail: notifyRequester ? rejectedByEmail : undefined,
            toEmails: notifyRequester ? toEmails : undefined,
            ccEmails: notifyRequester ? ccEmails : undefined,
            subject: notifyRequester ? subject : undefined,
            bodyHtml: notifyRequester ? (editor?.getHTML() ?? '') : undefined,
        };

        try {
            await usePost('ClosedLetterRequestReject', payload);
            setSuccessOpen(true);
            onRejected(request.id, reason, comments, rejectedByEmail);
            onClose();
        } catch (err) {
            setErrorMessage('Failed to reject request. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <React.Fragment>
            <Drawer
                open={open}
                anchor="right"
                onClose={handleClose}
                PaperProps={{
                    sx: { boxShadow: '-8px 0 32px rgba(22, 35, 58, 0.16)', height: '100vh', maxHeight: '100vh', overflow: 'hidden' },
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    height="100%"
                    width={notifyRequester ? 900 : 640}
                    maxWidth="95vw"
                    bgcolor={paper}
                    sx={{ fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif", overflow: 'hidden', transition: 'width 0.15s ease' }}
                >
                    {/* Letterhead */}
                    <Box
                        flexShrink={0}
                        sx={{
                            position: 'relative',
                            backgroundColor: '#007FFF',
                            color: '#fff',
                            padding: '16px 30px 14px',
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1.25} mb={0.25}>
                            <BlockIcon sx={{ fontSize: 18, color: '#d6ecff' }} />
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#d6ecff' }}>
                                Closed Letter Requests
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif", fontSize: '1.45rem', lineHeight: 1.15, fontWeight: 500 }}>
                            Decline Request
                        </Typography>
                        <IconButton
                            onClick={handleClose}
                            disabled={submitting}
                            sx={{ position: 'absolute', right: 12, top: 10, color: 'rgba(255,255,255,0.75)', '&:hover': { color: '#fff' } }}
                        >
                            <HighlightOffIcon style={{ fontSize: 24 }} />
                        </IconButton>
                    </Box>

                    {/* Case strip */}
                    {request && (
                        <Box padding="10px 30px" sx={{ backgroundColor: '#fff', borderBottom: `1px solid ${line}` }}>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8a9088' }}>
                                Regarding
                            </Typography>
                            <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: ink }}>
                                    {request.letterName}
                                </Typography>
                                <Typography sx={{ fontSize: '0.76rem', color: '#5b625b', fontFamily: mono }}>
                                    Debtor {request.debtorNumber} · Client {request.clientId}/{request.clientAccountNumber}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Body */}
                    <Box flexGrow={1} overflow="auto" padding="18px 30px">
                        <Box mb={1.5}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7368', mb: '4px', display: 'block' }}>
                                Rejection Reason
                            </Typography>
                            <Box
                                component="select"
                                value={reason}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setReason(e.target.value)}
                                sx={{
                                    width: '100%', boxSizing: 'border-box', border: `1px solid ${line}`, borderRadius: '4px',
                                    padding: '9px 12px', fontSize: '0.9rem', fontWeight: 600, color: reason ? ink : '#8a9088',
                                    fontFamily: 'inherit', backgroundColor: 'white', outline: 'none', appearance: 'auto',
                                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                    '&:focus': { borderColor: danger, boxShadow: `0 0 0 1px ${danger}` },
                                }}
                            >
                                <option value="" disabled>Select a reason</option>
                                {reasons.map((r) => (
                                    <option key={r.rejectReasonId} value={r.rejectReason}>{r.rejectReason}</option>
                                ))}
                            </Box>
                        </Box>

                        {reason && reasonHelp[reason] && (
                            <Box
                                display="flex"
                                alignItems="flex-start"
                                gap={1}
                                mb={1.5}
                                sx={{ backgroundColor: '#fff4e5', border: '1px solid #ffcc80', borderRadius: '4px', padding: '9px 12px' }}
                            >
                                <WarningAmberIcon fontSize="small" sx={{ color: '#b26a00', mt: '1px', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.8rem', color: '#7a4a00' }}>
                                    {reasonHelp[reason]}
                                </Typography>
                            </Box>
                        )}

                        <Box mb={1.5}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7368', mb: '4px', display: 'block' }}>
                                Comments{commentsRequired ? <Box component="span" sx={{ color: danger }}> — required</Box> : null}
                            </Typography>
                            <Box
                                component="textarea"
                                value={comments}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComments(e.target.value)}
                                placeholder={commentsRequired ? 'Required when reason is Other' : 'Optional — recorded for the audit trail'}
                                maxLength={1000}
                                sx={{
                                    width: '100%', boxSizing: 'border-box', border: `1px solid ${commentsRequired && !comments.trim() ? '#e5a5a0' : line}`,
                                    borderRadius: '4px', padding: '9px 12px', fontSize: '0.88rem', color: ink, fontFamily: 'inherit',
                                    backgroundColor: 'white', outline: 'none', resize: 'vertical', minHeight: 64, maxHeight: 140,
                                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                    '&:focus': { borderColor: danger, boxShadow: `0 0 0 1px ${danger}` },
                                }}
                            />
                        </Box>

                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ backgroundColor: '#fff', border: `1px solid ${line}`, borderRadius: '4px', padding: '10px 14px' }}
                        >
                            <Box>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: ink }}>
                                    Notify requester by email
                                </Typography>
                                <Typography sx={{ fontSize: '0.74rem', color: '#8a9088' }}>
                                    {reason === 'Cease and Desist'
                                        ? 'Disabled — Cease and Desist accounts receive no outbound contact.'
                                        : 'Sends a decline notice with the reason above. No attachment is included.'}
                                </Typography>
                            </Box>
                            <Switch
                                checked={notifyRequester}
                                disabled={reason === 'Cease and Desist'}
                                onChange={(e) => setNotifyRequester(e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: danger },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: danger },
                                }}
                            />
                        </Box>

                        {notifyRequester && (
                            <Box mt={2}>
                                <Box display="flex" gap={2} mb={1.5}>
                                    <RecipientField
                                        label="To"
                                        required
                                        placeholder="Recipient email, press Enter"
                                        emails={toEmails}
                                        input={toInput}
                                        onInputChange={setToInput}
                                        onCommit={() => commitRecipients(toInput, toEmails, setToEmails, setToInput)}
                                        onRemove={removeToEmail}
                                    />
                                    <RecipientField
                                        label="Cc"
                                        placeholder="Optional"
                                        emails={ccEmails}
                                        input={ccInput}
                                        onInputChange={setCcInput}
                                        onCommit={() => commitRecipients(ccInput, ccEmails, setCcEmails, setCcInput)}
                                        onRemove={removeCcEmail}
                                    />
                                </Box>

                                <Box mb={1.5}>
                                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7368', mb: '4px', display: 'block' }}>
                                        Subject
                                    </Typography>
                                    <Box
                                        component="input"
                                        value={subject}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
                                        sx={{
                                            width: '100%', boxSizing: 'border-box', border: `1px solid ${line}`, borderRadius: '4px',
                                            padding: '9px 12px', fontSize: '0.92rem', fontWeight: 600, color: ink,
                                            fontFamily: 'inherit', backgroundColor: 'white', outline: 'none',
                                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                            '&:focus': { borderColor: danger, boxShadow: `0 0 0 1px ${danger}` },
                                        }}
                                    />
                                </Box>

                                <Box>
                                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7368', mb: '4px', display: 'block' }}>
                                        Message
                                    </Typography>
                                    <Box
                                        sx={{
                                            backgroundColor: 'white', border: `1px solid ${line}`, borderRadius: '4px',
                                            height: 130, overflowY: 'auto', padding: '10px 14px',
                                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                            '&:focus-within': { borderColor: danger, boxShadow: `0 0 0 1px ${danger}` },
                                            '& .ProseMirror': { outline: 'none', minHeight: 110, fontSize: '0.9rem', color: ink, lineHeight: 1.6 },
                                            '& .ProseMirror p': { margin: '0 0 8px' },
                                        }}
                                    >
                                        <EditorContent editor={editor} />
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {errorMessage && (
                            <Typography sx={{ fontSize: '0.82rem', color: danger, mt: 2 }}>
                                {errorMessage}
                            </Typography>
                        )}
                    </Box>

                    {/* Footer */}
                    <Box
                        flexShrink={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        gap={1.5}
                        padding="10px 90px"
                        sx={{ backgroundColor: '#fff', borderTop: `1px solid ${line}` }}
                    >
                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={<DeleteOutlineIcon />}
                            disabled={submitting}
                            onClick={handleClose}
                            sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: '0.9rem',
                                padding: '9px 20px', borderRadius: '4px',
                                backgroundColor: '#5b625b', letterSpacing: '0.01em',
                                '&:hover': { backgroundColor: '#454a45' },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={<BlockIcon sx={{ fontSize: '18px !important' }} />}
                            disabled={!isValid || submitting}
                            onClick={handleConfirm}
                            sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: '0.9rem',
                                minWidth: 168, padding: '9px 20px', borderRadius: '4px',
                                backgroundColor: danger, letterSpacing: '0.01em',
                                '&:hover': { backgroundColor: dangerDeep },
                                '&.Mui-disabled': { backgroundColor: '#d7ded9', color: '#9aa39a' },
                            }}
                        >
                            {submitting ? 'Rejecting…' : 'Confirm Reject'}
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <Snackbar
                open={successOpen}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setSuccessOpen(false)}
            >
                <Alert onClose={() => setSuccessOpen(false)} severity="success" elevation={6} variant="filled">
                    Request rejected.
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
};

export default RejectRequestDrawer;
